import React from 'react';
import { Leaf, LogOut, ShoppingCart } from 'lucide-react';
import { User } from 'firebase/auth';
import { useCart } from '../../context';

interface HeaderProps {
    isAdmin: boolean;
    adminLoading: boolean;
    view: 'calendar' | 'admin';
    setView: (view: 'calendar' | 'admin') => void;
    setSelectedDay: (date: Date) => void;
    // Auth props
    user: User | null;
    isGoogleUser: boolean;
    onSignIn: () => void;
    onSignOut: () => void;
    // Cart props
    onOpenCart: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
    isAdmin, 
    adminLoading,
    view, 
    setView, 
    setSelectedDay,
    user,
    isGoogleUser,
    onSignIn,
    onSignOut,
    onOpenCart
}) => {
    const { cartCount } = useCart();
    const userDisplayName = user?.displayName || user?.email?.split('@')[0] || 'User';
    const userPhotoURL = user?.photoURL;

    return (
        <header className="bg-slate-900 border-b border-white/10 p-4 sticky top-0 z-40 backdrop-blur-md bg-opacity-90">
            <div className="max-w-4xl mx-auto flex justify-between items-center">
              <div className="flex items-center gap-2">
                {/* Logo Icon in Rose/Fuchsia */}
                <Leaf className="text-rose-500" />
                <h1 
                   onClick={() => setSelectedDay(new Date())} 
                   className="text-xl md:text-3xl tracking-wide cursor-pointer hover:text-white transition-colors bg-clip-text text-transparent bg-gradient-to-r from-rose-400 to-fuchsia-400" 
                   style={{ fontFamily: "'Brush Script MT', 'Segoe Script', 'Gabriola', cursive" }}
                >
                  Yoga Classes with Kat
                </h1>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                {/* Cart Button */}
                <button
                  onClick={onOpenCart}
                  className="relative text-slate-400 hover:text-white transition-colors p-1 flex-shrink-0"
                  title="View Cart"
                >
                  <ShoppingCart size={20} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount > 9 ? '9+' : cartCount}
                    </span>
                  )}
                </button>

                {/* Auth Section */}
                {isGoogleUser ? (
                  // Signed in with Google
                  <>
                    {/* User avatar - always visible */}
                    {userPhotoURL ? (
                      <img 
                        src={userPhotoURL} 
                        alt={userDisplayName}
                        className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-fuchsia-500 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-fuchsia-600 flex items-center justify-center text-white text-xs md:text-sm font-bold flex-shrink-0">
                        {userDisplayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {/* Name and admin badge - hidden on mobile */}
                    <div className="hidden md:flex items-center gap-2">
                      <span className="text-slate-300 text-sm">
                        {userDisplayName}
                      </span>
                      {adminLoading ? (
                        <span className="text-slate-500 text-xs">...</span>
                      ) : isAdmin ? (
                        <span className="text-emerald-400 text-xs uppercase tracking-wider font-semibold">
                          Admin
                        </span>
                      ) : null}
                    </div>
                    
                    {/* View toggle for admins */}
                    {isAdmin && (
                      <button 
                        onClick={() => setView(view === 'calendar' ? 'admin' : 'calendar')}
                        className="bg-fuchsia-700 hover:bg-fuchsia-600 px-2 md:px-3 py-1 rounded text-xs md:text-sm transition-colors text-white shadow-lg shadow-fuchsia-900/20 flex-shrink-0"
                      >
                        {view === 'calendar' ? 'Dashboard' : 'Calendar'}
                      </button>
                    )}
                    
                    {/* Logout button - always visible, after toggle */}
                    <button
                      onClick={onSignOut}
                      className="text-slate-400 hover:text-white transition-colors p-1 flex-shrink-0"
                      title="Sign Out"
                    >
                      <LogOut size={18} />
                    </button>
                  </>
                ) : (
                  // Anonymous user - show sign in button
                  <button 
                    onClick={onSignIn}
                    className="text-slate-400 hover:text-white transition-colors text-xs uppercase tracking-wider font-semibold flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="hidden sm:inline">Sign In</span>
                  </button>
                )}
              </div>
            </div>
        </header>
    );
};

export default Header;
